import React, { PropsWithChildren } from 'react';

import { useQuery } from '@apollo/client';
import { Divider, Flex, Text } from '@opendoor/bricks/core';

import { OdProtosWebData_RecordedFeature } from '../../__generated__/athenaGlobalTypes';
import {
  LeadDetails,
  LeadDetailsVariables,
} from '../../graphql/athena/queries/__generated__/LeadDetails';
import { LEAD_DETAILS_QUERY } from '../../graphql/athena/queries/LeadDetails';
import { customerDataProps, getIsALALead } from '../../helpers/getAllLeadData';
import { QuestionnaireTask } from './QuestionnaireTask';
import { TaskProps, VideoInteraction } from './VideoInteraction';

interface SSVAFormProps {
  sellerLeadToken: string;
  customerData: customerDataProps;
}

const interiorTask: TaskProps = {
  title: 'Interior video',
  subtitle: '(8 mins)',
  recordedFeature: OdProtosWebData_RecordedFeature.INTERIOR,
};

const frontyardTask: TaskProps = {
  title: 'Video of the front yard',
  subtitle: '(1 min)',
  recordedFeature: OdProtosWebData_RecordedFeature.FRONTYARD,
};

const backyardTask: TaskProps = {
  title: 'Video of the backyard',
  subtitle: '(1 min)',
  recordedFeature: OdProtosWebData_RecordedFeature.BACKYARD,
};

export const SSVAForm: React.FC<SSVAFormProps & PropsWithChildren> = ({
  sellerLeadToken,
  customerData,
}) => {
  const { error, data } = useQuery<LeadDetails, LeadDetailsVariables>(LEAD_DETAILS_QUERY, {
    context: {
      clientName: 'athena',
    },
    variables: { selldirectLeadToken: sellerLeadToken ?? '' },
  });
  if (error) return null;
  const sellerInputUUID =
    data?.odProtosListingsServicesListwithcertaintyproduct.getSellDirectDetails?.lead
      ?.sellerInputUuid ?? '';
  if (!sellerInputUUID) return null;

  const isALALead = getIsALALead(data);
  if (!isALALead) return null;

  return (
    <Flex
      flexDirection="column"
      fontSize="s2"
      my={3}
      backgroundColor="neutrals20"
      borderRadius="semiRounded"
      p={3}
    >
      <Text fontWeight="semibold">Assessment Details</Text>
      <VideoInteraction sellerInputUUID={sellerInputUUID} task={interiorTask} />
      <VideoInteraction sellerInputUUID={sellerInputUUID} task={frontyardTask} />
      <VideoInteraction sellerInputUUID={sellerInputUUID} task={backyardTask} />
      <QuestionnaireTask
        sellerInputUUID={sellerInputUUID}
        sellerLeadToken={sellerLeadToken}
        customerData={customerData}
      />
      <Divider my={3} />
    </Flex>
  );
};
